import { useEffect, useRef } from 'react';
import { useConnectionContext } from '../contexts/ConnectionStateContext';
import { resolveConnectionNote } from '../utils/connectionNote';

/**
 * useConnectionNote — the single note for a connection, with the legacy
 * `notes` column draining into `nickname` the first time the connection is
 * opened after the Note/Nickname merge (2026-08-18 Detail frames).
 *
 * Order matters: the merged text is written to the surviving field FIRST, and
 * the legacy note is cleared only once that write is confirmed. updateNickname
 * returns false when the DB write fails and deliberately skips its own cache
 * update, so a failure leaves the old note exactly where it was — the merge
 * can lose text only by clearing before writing, which is why it never does.
 *
 * @param {string} chatRoomId
 * @param {object} connectionData - the connection's context record
 * @param {boolean} isProfileUser - which note key the clear targets
 * @returns {string} the text the one field shows
 */
const useConnectionNote = (chatRoomId, connectionData, isProfileUser = true) => {
  const { updateNickname, updateNotes } = useConnectionContext();
  // Migration is per-connection, not per-render: mount paints the cache and the
  // DB refresh lands after, so the resolve runs several times per view.
  const migratedRef = useRef(null);

  const { text, needsMigration } = resolveConnectionNote({
    nickname: connectionData?.nickname,
    notes: connectionData?.notes,
  });

  useEffect(() => {
    if (!chatRoomId || !needsMigration) return;
    if (migratedRef.current === chatRoomId) return;
    migratedRef.current = chatRoomId;

    let cancelled = false;
    (async () => {
      const ok = await updateNickname(chatRoomId, text);
      if (!ok || cancelled) return;
      await updateNotes(chatRoomId, '', isProfileUser);
    })();

    return () => {
      cancelled = true;
    };
  }, [chatRoomId, needsMigration, text, updateNickname, updateNotes, isProfileUser]);

  return text;
};

export default useConnectionNote;
